import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check, Shield } from 'lucide-react';
import { initGA } from '../utils/analytics';
import './CookieConsent.css';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,    // Always true - can't be disabled
    analytics: false,   // Google Analytics tracking
    functional: false   // Future: functional cookies
  });

  useEffect(() => {
    // Don't show cookie consent in miniapp mode
    const isMiniApp = window?.parent !== window || 
                      navigator.userAgent.includes('MiniApp') ||
                      window.location.search.includes('miniapp=true');
                      
    if (isMiniApp) {
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    const consentTimestamp = localStorage.getItem('cookie-consent-timestamp');
    
    if (!consent) {
      // Show banner if no previous consent
      setTimeout(() => setIsVisible(true), 1000); // Small delay for better UX
    } else {
      // Check if consent is older than 365 days (GDPR requirement)
      const consentDate = new Date(parseInt(consentTimestamp));
      const now = new Date();
      const daysDiff = (now - consentDate) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > 365) {
        // Consent expired, show banner again
        setTimeout(() => setIsVisible(true), 1000);
      } else {
        // Load existing preferences and initialize analytics if consented
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        
        if (savedPreferences.analytics) {
          initGA();
        }
      }
    }
  }, []);

  const saveConsent = (consentPreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(consentPreferences));
    localStorage.setItem('cookie-consent-timestamp', Date.now().toString());
    setPreferences(consentPreferences);
    
    // Initialize analytics if user consented
    if (consentPreferences.analytics) {
      initGA();
    }
    
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      functional: true
    });
  };

  const handleAcceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      functional: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="cookie-consent-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="cookie-consent-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <div className="cookie-consent-content">
            <div className="cookie-consent-header">
              <div className="cookie-consent-icon">
                <Cookie size={24} />
              </div>
              <h3>Your Privacy Matters</h3>
              <button
                className="cookie-consent-close"
                onClick={() => setIsVisible(false)}
                aria-label="Close cookie consent"
              >
                <X size={20} />
              </button>
            </div>

            {!showSettings ? (
              // Main consent view
              <div className="cookie-consent-main">
                <p className="cookie-consent-description">
                  We use cookies to enhance your experience and analyze site usage to help us improve our confession platform. 
                  Your testimony data is never tracked or shared.
                </p>
                
                <div className="cookie-consent-actions">
                  <button
                    className="btn btn-secondary cookie-btn"
                    onClick={handleAcceptNecessary}
                  >
                    Necessary Only
                  </button>
                  <button
                    className="btn btn-ghost cookie-btn"
                    onClick={() => setShowSettings(true)}
                  >
                    <Settings size={16} />
                    Customize
                  </button>
                  <button
                    className="btn btn-primary cookie-btn"
                    onClick={handleAcceptAll}
                  >
                    <Check size={16} />
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Settings view
              <div className="cookie-consent-settings">
                <div className="cookie-categories">
                  
                  {/* Necessary Cookies */}
                  <div className="cookie-category">
                    <div className="cookie-category-header">
                      <div className="cookie-category-info">
                        <h4>Necessary Cookies</h4>
                        <span className="cookie-category-badge necessary">Always Active</span>
                      </div>
                      <div className="cookie-toggle disabled">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          id="necessary"
                        />
                        <label htmlFor="necessary"></label>
                      </div>
                    </div>
                    <p className="cookie-category-description">
                      Essential for the website to function properly. These cannot be disabled.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="cookie-category">
                    <div className="cookie-category-header">
                      <div className="cookie-category-info">
                        <h4>Analytics Cookies</h4>
                        <span className="cookie-category-badge analytics">Optional</span>
                      </div>
                      <div className="cookie-toggle">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                          id="analytics"
                        />
                        <label htmlFor="analytics"></label>
                      </div>
                    </div>
                    <p className="cookie-category-description">
                      Help us understand how visitors interact with our platform (Google Analytics). 
                      No personal confession content is tracked.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="cookie-category">
                    <div className="cookie-category-header">
                      <div className="cookie-category-info">
                        <h4>Functional Cookies</h4>
                        <span className="cookie-category-badge functional">Optional</span>
                      </div>
                      <div className="cookie-toggle">
                        <input
                          type="checkbox"
                          checked={preferences.functional}
                          onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                          id="functional"
                        />
                        <label htmlFor="functional"></label>
                      </div>
                    </div>
                    <p className="cookie-category-description">
                      Enable enhanced functionality like remembering your preferences (future feature).
                    </p>
                  </div>

                </div>

                <div className="cookie-consent-actions">
                  <button
                    className="btn btn-secondary cookie-btn"
                    onClick={() => setShowSettings(false)}
                  >
                    Back
                  </button>
                  <button
                    className="btn btn-primary cookie-btn"
                    onClick={handleSavePreferences}
                  >
                    <Shield size={16} />
                    Save Preferences
                  </button>
                </div>
              </div>
            )}

            <div className="cookie-consent-footer">
              <p className="cookie-consent-legal">
                By continuing to use our site, you agree to our use of cookies as described in our{' '}
                <a href="#privacy" className="cookie-consent-link">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;